/**
 * Render an unknown thrown / rejected value as a human-readable string.
 *
 * Why this exists: throwing a Supabase PostgrestError (or an `error` object
 * from `supabase.auth.*`) produces something that fails `e instanceof Error`,
 * so the previous shorthand `e instanceof Error ? e.message : String(e)`
 * silently rendered `[object Object]`. This helper reaches into the common
 * shapes (Error, Supabase, fetch Response, plain objects with .message)
 * before falling back to JSON.
 */
export function formatError(err: unknown): string {
  if (err == null) return "Unknown error.";
  if (typeof err === "string") return err;

  if (err instanceof Error && err.message) return err.message;

  if (typeof err === "object") {
    const e = err as Record<string, unknown>;
    if (typeof e.message === "string" && e.message) return e.message;
    if (typeof e.error_description === "string" && e.error_description) {
      return e.error_description;
    }
    if (typeof e.error === "string" && e.error) return e.error;
    if (typeof e.hint === "string" && e.hint) return e.hint;
    if (typeof e.details === "string" && e.details) return e.details;
    try {
      const s = JSON.stringify(err);
      if (s && s !== "{}") return s;
    } catch {
      /* circular ref — fall through */
    }
  }

  return String(err);
}
